// Leitura da georreferenciação de um modelo IFC.
//
// Três fontes, por ordem de precisão:
//   1. IfcMapConversion + IfcProjectedCRS: a georreferenciação a sério
//      (o "nível 50" do guia da buildingSMART), com origem projectada,
//      rotação do norte da quadrícula e escala.
//   2. IfcSite RefLatitude / RefLongitude: coordenadas geográficas
//      aproximadas, suficientes para situar o edifício num mapa mas não
//      para sobrepor a levantamentos.
//   3. TrueNorth do contexto de representação: para onde aponta o norte
//      verdadeiro em relação ao +Y do modelo.
//
// Um modelo pode ter as três, uma, ou nenhuma. O relatório diz o que
// encontrou e cala-se sobre o resto, em vez de assumir omissões.

import type { IfcReadSource } from './source.js'
import { dms, num, round, str, val } from './values.js'

export interface GeoRefInfo {
  /** IfcMapConversion presente. */
  mapConversion: boolean
  eastings: number | null
  northings: number | null
  orthogonalHeight: number | null
  /** Rotação do +X do modelo em relação ao este da quadrícula, em graus. */
  rotationDeg: number | null
  scale: number | null
  /** Nome do IfcProjectedCRS, tipicamente 'EPSG:3763'. */
  crsName: string | null
  crsDescription: string | null
  /** IfcSite RefLatitude / RefLongitude, em graus decimais. */
  latitude: number | null
  longitude: number | null
  refElevation: number | null
  /** Norte verdadeiro, em graus a partir do +Y do modelo. */
  trueNorthDeg: number | null
}

export function emptyGeoRef(): GeoRefInfo {
  return {
    mapConversion: false,
    eastings: null,
    northings: null,
    orthogonalHeight: null,
    rotationDeg: null,
    scale: null,
    crsName: null,
    crsDescription: null,
    latitude: null,
    longitude: null,
    refElevation: null,
    trueNorthDeg: null,
  }
}

export function readGeoRef(src: IfcReadSource): GeoRefInfo {
  const g = emptyGeoRef()

  for (const id of src.getLineIDsByType('IFCMAPCONVERSION')) {
    const mc = src.getEntity(id)
    if (!mc) continue
    g.mapConversion = true
    g.eastings = num(mc.Eastings)
    g.northings = num(mc.Northings)
    g.orthogonalHeight = num(mc.OrthogonalHeight)
    g.scale = num(mc.Scale)
    const xa = num(mc.XAxisAbscissa)
    const xo = num(mc.XAxisOrdinate)
    if (xa !== null && xo !== null && (xa !== 0 || xo !== 0)) {
      g.rotationDeg = round((Math.atan2(xo, xa) * 180) / Math.PI, 6)
    }
    const crs = mc.TargetCRS as Record<string, unknown> | undefined
    if (crs) {
      g.crsName = str(crs.Name)
      g.crsDescription = str(crs.Description)
    }
    break
  }

  for (const id of src.getLineIDsByType('IFCSITE')) {
    const site = src.getEntity(id)
    if (!site) continue
    const lat = dms(site.RefLatitude)
    const lon = dms(site.RefLongitude)
    if (lat !== null) g.latitude = round(lat, 8)
    if (lon !== null) g.longitude = round(lon, 8)
    g.refElevation = num(site.RefElevation)
    if (lat !== null || lon !== null) break
  }

  for (const id of src.getLineIDsByType('IFCGEOMETRICREPRESENTATIONCONTEXT')) {
    const ctx = src.getEntity(id)
    const tn = ctx?.TrueNorth as Record<string, unknown> | undefined
    const ratios = tn ? val(tn.DirectionRatios) : null
    if (Array.isArray(ratios) && ratios.length >= 2) {
      const x = num(ratios[0]) ?? 0
      const y = num(ratios[1]) ?? 0
      if (x !== 0 || y !== 0) {
        g.trueNorthDeg = round((Math.atan2(x, y) * 180) / Math.PI, 6)
        break
      }
    }
  }

  return g
}

/** Unidades declaradas no IfcProject, por grandeza ('length', 'area'...). */
export function readUnits(src: IfcReadSource): Record<string, string> {
  const out: Record<string, string> = {}
  for (const id of src.getLineIDsByType('IFCPROJECT')) {
    const proj = src.getEntity(id)
    const ctx = proj?.UnitsInContext as Record<string, unknown> | undefined
    const units = ctx?.Units
    if (!Array.isArray(units)) continue
    for (const raw of units) {
      if (!raw || typeof raw !== 'object') continue
      const u = raw as Record<string, unknown>
      const label = unitLabel(u)
      if (!label) continue
      const type = str(u.UnitType)
      const key = (type ?? 'UNIT').replace(/UNIT$/i, '').toLowerCase() || 'unit'
      if (!out[key]) out[key] = label
    }
  }
  return out
}

function unitLabel(u: Record<string, unknown>): string | null {
  const cls = String(u.type ?? '').toUpperCase()
  if (cls === 'IFCSIUNIT') {
    // MILLI + METRE lê-se "millimetre": o prefixo é a palavra, não o
    // símbolo, por isso a concatenação simples está certa.
    const name = str(u.Name)?.toLowerCase() ?? '?'
    const prefix = str(u.Prefix)?.toLowerCase() ?? ''
    return `${prefix}${name}`
  }
  if (
    cls === 'IFCCONVERSIONBASEDUNIT' ||
    cls === 'IFCCONVERSIONBASEDUNITWITHOFFSET'
  ) {
    return str(u.Name) ?? 'unidade convertida'
  }
  if (cls === 'IFCMONETARYUNIT') return str(u.Currency)
  return null
}
